/**
 * Pure state transitions for PdfViewer's password prompt, extracted so this logic is
 * unit-testable without pulling in `pdfjs-dist` (its ESM build uses `import.meta`, which
 * isn't importable under this repo's Jest config).
 */

export interface PasswordAttemptState {
  /** What's currently typed into the field. */
  input: string;
  /** The value pdf.js is validating, or null when no attempt is in flight. */
  submitted: string | null;
}

export const initialPasswordAttemptState: PasswordAttemptState = {
  input: "",
  submitted: null,
};

/**
 * Whether Unlock/Enter should be allowed to fire right now. Blocking a second submit while
 * one is already in flight is what keeps `submitted` unambiguous -- pdf.js's eventual
 * rejection doesn't say which attempt it's for, so at most one may ever be outstanding.
 */
export function canSubmit(state: PasswordAttemptState): boolean {
  return state.input.length > 0 && state.submitted === null;
}

/** Records a submission. Caller is responsible for actually forwarding it to pdf.js. */
export function submit(state: PasswordAttemptState): PasswordAttemptState {
  return { ...state, submitted: state.input };
}

/**
 * pdf.js rejected the in-flight attempt. Only wipe the field if it still holds exactly what
 * was submitted -- a retype started while validation was pending is a fresh, not-yet-judged
 * attempt and must survive untouched.
 */
export function onIncorrectPassword(
  state: PasswordAttemptState
): PasswordAttemptState {
  return {
    input: state.input === state.submitted ? "" : state.input,
    submitted: null,
  };
}
